// hooks/useInvites.ts — 1.24.00 personal invite links (B2).
//
//   useInviteLink()      the signed-in member's own code + regenerate
//   useInviteInfo(code)  public lookup for /join/<code>
//
// Components must NOT import invitesService directly.

import { useCallback, useEffect, useState } from 'react';
import { invitesService } from '@/core/services/invitesService';
import type { InviteInfo, InviteLink } from '@/core/models/account';

export interface InviteLinkHook {
  invite: InviteLink | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch the current code (e.g. after a failed load). */
  refresh: () => void;
  /** Retire the current code and mint a new one. Resolves true on success. */
  regenerate: () => Promise<boolean>;
}

export function useInviteLink(): InviteLinkHook {
  const [invite, setInvite] = useState<InviteLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flag
    setLoading(true);
    invitesService
      .mine()
      .then((r) => { if (alive) { setInvite(r); setError(null); } })
      .catch(() => { if (alive) setError('Could not load your invite link.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const regenerate = useCallback(async () => {
    try {
      const next = await invitesService.regenerate();
      setInvite(next);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { invite, loading, error, refresh, regenerate };
}

export interface InviteInfoHook {
  info: InviteInfo | null;
  loading: boolean;
  /** 'not_found' for an unknown/revoked code (404), 'error' for anything else. */
  error: 'not_found' | 'error' | null;
}

export function useInviteInfo(code: string | null | undefined): InviteInfoHook {
  const [state, setState] = useState<InviteInfoHook>({ info: null, loading: !!code, error: null });
  useEffect(() => {
    if (!code) {
      const t = setTimeout(() => setState({ info: null, loading: false, error: 'not_found' }), 0);
      return () => clearTimeout(t);
    }
    let alive = true;
    invitesService.lookup(code).then(
      (info) => { if (alive) setState({ info, loading: false, error: null }); },
      (e: unknown) => {
        if (!alive) return;
        const status = (e as { status?: number } | null)?.status;
        setState({ info: null, loading: false, error: status === 404 ? 'not_found' : 'error' });
      },
    );
    return () => { alive = false; };
  }, [code]);
  return state;
}
